import type { LeavePageView } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Loading } from "../components/ui";

/** Texted to a nurse when the door closed: block off the rest of today (no sign-in; the link is signed). */
export function LeavePage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();
  const url = `/api/leave/${encodeURIComponent(token)}`;
  const page = useQuery({ queryKey: ["leave", token], queryFn: () => get<LeavePageView>(url), retry: false });
  const block = useMutation({ mutationFn: () => post<LeavePageView>(url), onSuccess: (v) => qc.setQueryData(["leave", token], v) });
  const p = page.data;
  useEffect(() => {
    document.title = p ? `Leaving for the day · ${p.clinicName}` : "Leaving for the day";
  }, [p]);
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-ink px-4 py-4 text-white">
        <div className="mx-auto max-w-lg font-display text-xl">{p?.clinicName ?? " "}</div>
      </header>
      <main className="mx-auto flex max-w-lg flex-col gap-4 px-4 py-6">
        {page.error ? (
          <Alert>{errorMessage(page.error)}</Alert>
        ) : !p ? (
          <Loading />
        ) : p.blocked ? (
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <div className="grid size-14 place-items-center rounded-full bg-ok-wash text-2xl text-ok">✓</div>
            <h1 className="font-display text-2xl">The rest of today is blocked off</h1>
            <p className="text-ink-soft">
              No online bookings with you from {p.from} to {p.to}. Have a good evening, {p.firstName}! To undo it, remove the time off on the Availability page.
            </p>
          </div>
        ) : p.visitsLater.length ? (
          <>
            <h1 className="font-display text-2xl">You still have visits today</h1>
            <p className="text-ink-soft">{p.visitsLater.join(", ")}. The rest of the day can't be blocked off while they're booked.</p>
          </>
        ) : (
          <>
            <h1 className="font-display text-2xl">Done for the day, {p.firstName}?</h1>
            <p className="text-ink-soft">
              Blocking off the rest of {p.dayLabel} stops online bookings with you from <strong>{p.from}</strong> to <strong>{p.to}</strong>. You can undo it on the Availability page.
            </p>
            {block.error && <Alert>{errorMessage(block.error)}</Alert>}
            <Button variant="primary" className="py-3" busy={block.isPending} onClick={() => block.mutate()}>
              Block off the rest of today
            </Button>
          </>
        )}
      </main>
    </div>
  );
}
